/**
 * Domain auto-join (plan 2026-07-24-domain-auto-join) — the workspace owner
 * claims the domain of their own verified email; anyone who later signs up
 * (or logs in) with a verified email at that domain joins this workspace
 * automatically as a member.
 *
 * ALL validation lives server-side (PUT /api/workspaces/domain: format,
 * public-provider blocklist, owner role, verified-email match, global
 * uniqueness) — this panel only maps its error codes to copy and gates the
 * controls to owners, mirroring MembersPanel's owner model: members see a
 * read-only status line, never the controls.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { SectionCard } from '@/components/patterns/section-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, workspaceApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { useActiveOrg } from '@/lib/use-active-org';

/** Server error codes from PUT /api/workspaces/domain → user-facing copy. */
const DOMAIN_ERROR_MESSAGES: Record<string, string> = {
  domain_mismatch: 'You can only claim the domain of your own verified email',
  email_unverified: 'Verify your email before claiming a domain',
  public_domain_blocked: 'Public email providers cannot be claimed',
  domain_taken: 'Another workspace already claims this domain',
  invalid_domain: 'Enter a valid domain like example.com',
  owner_required: 'Only the workspace owner can change this',
};

function domainErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    if (code && DOMAIN_ERROR_MESSAGES[code]) return DOMAIN_ERROR_MESSAGES[code];
  }
  return fallback;
}

export function DomainPanel() {
  const qc = useQueryClient();
  // Shares one roster fetch with MembersPanel and the nav (useActiveOrg).
  const { isOwner, isLoading: orgLoading } = useActiveOrg();

  const { data } = useQuery({ queryKey: qk.workspace, queryFn: () => workspaceApi.get() });
  const workspace = data?.workspace;
  const claimedDomain = workspace?.autoJoinDomain ?? null;

  const [domainInput, setDomainInput] = useState('');
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  const enable = useMutation({
    mutationFn: (domain: string) => workspaceApi.setDomain(domain),
    onSuccess: (res) => {
      qc.setQueryData(qk.workspace, res);
      toast.success(`Auto-join enabled for @${res.workspace.autoJoinDomain}`);
      setDomainInput('');
    },
    onError: (err) => toast.error(domainErrorMessage(err, 'Failed to enable auto-join')),
  });

  const disable = useMutation({
    mutationFn: () => workspaceApi.setDomain(null),
    onSuccess: (res) => {
      qc.setQueryData(qk.workspace, res);
      toast.success('Domain auto-join disabled');
      setConfirmingDisable(false);
    },
    onError: (err) => toast.error(domainErrorMessage(err, 'Failed to disable auto-join')),
  });

  return (
    <SectionCard
      title="Domain auto-join"
      description="Anyone who signs up with a verified @your-domain email joins this workspace automatically as a member."
    >
      {!workspace || orgLoading ? (
        <Skeleton className="h-11 w-full" />
      ) : claimedDomain ? (
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">@{claimedDomain}</p>
            <p className="text-xs text-muted-foreground">
              New sign-ups with a verified @{claimedDomain} email join automatically as members.
            </p>
          </div>
          {isOwner ? (
            confirmingDisable ? (
              <div className="flex gap-1">
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={disable.isPending}
                  onClick={() => disable.mutate()}
                >
                  {disable.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmingDisable(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setConfirmingDisable(true)}>
                Disable
              </Button>
            )
          ) : null}
        </div>
      ) : isOwner ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const domain = domainInput.trim();
            if (domain) enable.mutate(domain);
          }}
          className="flex gap-2"
        >
          <Input
            aria-label="Email domain"
            placeholder="example.com"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
          />
          <Button type="submit" size="sm" disabled={enable.isPending || !domainInput.trim()}>
            {enable.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enable'}
          </Button>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground">
          Domain auto-join is not enabled. Only the workspace owner can enable it.
        </p>
      )}
    </SectionCard>
  );
}
